import moment from "moment";

export interface SettledElapsedTimeInfo {
  isSettled: boolean;
  isJustSettled: boolean;
  relativeText: string;
  fullText: string;
  tooltip: string;
}

const STORAGE_PREFIX = "st-admin:last-settled";

function buildStorageKey(
  mallId: string,
  shopNo: string | number,
  subKey: string
): string {
  return `${STORAGE_PREFIX}:${mallId}:${String(shopNo)}:${subKey}`;
}

/**
 * 특정 상점 및 기간의 정산 완료 시각을 localStorage에 저장합니다.
 */
export function saveSettlementTime(
  mallId: string,
  shopNo: string | number,
  startDate: string,
  endDate: string,
  timestamp: number = Date.now()
): void {
  if (!mallId || shopNo === undefined || !startDate || !endDate) return;

  try {
    const rangeKey = buildStorageKey(
      mallId,
      shopNo,
      `range:${startDate}_${endDate}`
    );
    window.localStorage.setItem(rangeKey, String(timestamp));

    // 시작일과 종료일이 같은 월에 속하는 경우 월별 키도 업데이트
    const startMonth = startDate.slice(0, 7);
    const endMonth = endDate.slice(0, 7);
    if (startMonth === endMonth) {
      const monthKey = buildStorageKey(mallId, shopNo, `month:${startMonth}`);
      const prevMonthTime = Number(window.localStorage.getItem(monthKey) || 0);
      if (timestamp > prevMonthTime) {
        window.localStorage.setItem(monthKey, String(timestamp));
      }
    }

    // 상점 전역 최신 정산 시각 업데이트
    const latestKey = buildStorageKey(mallId, shopNo, "latest");
    const prevLatest = Number(window.localStorage.getItem(latestKey) || 0);
    if (timestamp > prevLatest) {
      window.localStorage.setItem(latestKey, String(timestamp));
    }
  } catch (e) {
    console.warn("Failed to persist settlement time to localStorage:", e);
  }
}

/**
 * 특정 상점 및 기간의 정산 완료 시각을 가져옵니다.
 */
export function getSettlementTime(
  mallId: string,
  shopNo: string | number,
  startDate: string,
  endDate: string
): number | null {
  if (!mallId || shopNo === undefined || !startDate || !endDate) return null;

  try {
    const rangeKey = buildStorageKey(
      mallId,
      shopNo,
      `range:${startDate}_${endDate}`
    );
    const stored = window.localStorage.getItem(rangeKey);
    if (stored) {
      const num = Number(stored);
      if (!Number.isNaN(num) && num > 0) return num;
    }

    const startMonth = startDate.slice(0, 7);
    const endMonth = endDate.slice(0, 7);
    if (startMonth === endMonth) {
      const monthKey = buildStorageKey(mallId, shopNo, `month:${startMonth}`);
      const monthStored = window.localStorage.getItem(monthKey);
      if (monthStored) {
        const num = Number(monthStored);
        if (!Number.isNaN(num) && num > 0) return num;
      }
    }
  } catch (e) {
    console.warn("Failed to retrieve settlement time from localStorage:", e);
  }

  return null;
}

/**
 * 특정 월(YYYY-MM)의 DB 결과 목록과 스토리지 캐시를 종합하여 가장 최근 정산 시각을 구합니다.
 */
export function getLatestSettledTimeForMonth(
  mallId: string,
  shopNo: string | number,
  monthStr: string,
  dbResults?: Array<{ date: string; updated_at?: number }>
): number | null {
  const timestamps: number[] = [];

  // 1. DB 결과에서 해당 월에 속한 일별 updated_at 수집
  if (Array.isArray(dbResults)) {
    for (const r of dbResults) {
      if (r.date && r.date.startsWith(monthStr) && r.updated_at) {
        timestamps.push(r.updated_at);
      }
    }
  }

  // 2. localStorage의 월별 키 확인
  if (mallId && shopNo !== undefined && monthStr) {
    try {
      const monthKey = buildStorageKey(mallId, shopNo, `month:${monthStr}`);
      const monthStored = window.localStorage.getItem(monthKey);
      if (monthStored) {
        const num = Number(monthStored);
        if (!Number.isNaN(num) && num > 0) {
          timestamps.push(num);
        }
      }
    } catch (e) {
      console.warn("Failed to check month settlement time:", e);
    }
  }

  if (timestamps.length === 0) return null;
  return Math.max(...timestamps);
}

/**
 * 타임스탬프를 기준으로 현재까지 경과한 시간 및 툴팁 정보를 포맷팅합니다.
 */
export function formatSettledElapsedTime(
  timestamp: number | null | undefined,
  now: number = Date.now()
): SettledElapsedTimeInfo {
  if (!timestamp || Number.isNaN(timestamp) || timestamp <= 0) {
    return {
      isSettled: false,
      isJustSettled: false,
      relativeText: "미정산",
      fullText: "정산 이력 없음",
      tooltip:
        "정산 이력이 없습니다. 정산하기 버튼을 눌러 데이터를 불러오세요.",
    };
  }

  const diffMs = Math.max(0, now - timestamp);
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const isJustSettled = diffSec < 60;

  let relativeText = "";
  if (diffSec < 60) {
    relativeText = "방금 전";
  } else if (diffMin < 60) {
    relativeText = `${diffMin}분 전`;
  } else if (diffHour < 24) {
    relativeText = `${diffHour}시간 전`;
  } else if (diffDay < 7) {
    relativeText = `${diffDay}일 전`;
  } else {
    relativeText = moment(timestamp).format("M월 D일");
  }

  const fullText = moment(timestamp).format("YYYY-MM-DD HH:mm:ss");
  const tooltip = isJustSettled
    ? `마지막 정산 일시: ${fullText} (방금 정산이 완료되었습니다. 1분 후 다시 정산할 수 있습니다.)`
    : `마지막 정산 일시: ${fullText} (${relativeText})`;

  return {
    isSettled: true,
    isJustSettled,
    relativeText,
    fullText,
    tooltip,
  };
}
